/* lib/buildWatch.js — открытая вкладка узнаёт, что на сервере новая сборка.

   Вкладка, у которой все нужные чанки уже загружены, живёт на своей сборке сколько угодно:
   восстановление после выката (lib/chunkReload.js) срабатывает только на сбой загрузки чанка.
   Кейс 03.11.2026 (dev, чат 2937): Safari сутками держал сборку 31.10 — «Блоки ассистента»
   показывались сырым JSON, люди искали баг в новой фиче. Правила:
   · сборку узнаём по главному бандлу: `/assets/index-<хеш>.js` в index.html — тот, что
     загружен (тег <script type="module"> документа), против того, что отдаёт сервер сейчас;
   · сверяем при возврате во вкладку (focus / visibilitychange), раз в CHECK_EVERY_MS, пока
     вкладка видна, и один раз через FIRST_CHECK_MS после загрузки (страница могла подняться
     из кэша браузера) — не чаще раза в MIN_GAP_MS; скрытая вкладка сеть не трогает;
   · запрос index.html — голый fetch мимо apiFetch НАРОЧНО: это не API (ни сессии, ни
     пространства), и он не должен считаться запросом «в полёте» — иначе мешал бы тишине сети
     перехода вкладки (WorkspaceTabSync);
   · о новой сборке говорим один раз на сборку; перезагружает человек (несохранённое
     сторожит beforeunload UnsavedChangesProvider), сами не перезагружаем;
   · без сборки (vite dev-сервер: бандла /assets/index-* нет) — не сверяем. */

export const CHECK_EVERY_MS = 15 * 60 * 1000;
export const MIN_GAP_MS = 60 * 1000;
export const FIRST_CHECK_MS = 20 * 1000;

const ENTRY_RE = /\/assets\/index-[\w-]+\.js/;

/** Главный бандл в тексте index.html: '/assets/index-<хеш>.js' или null. */
export function entryFromHtml(html) {
  const m = typeof html === 'string' ? html.match(ENTRY_RE) : null;
  return m ? m[0] : null;
}

/** Главный бандл, которым живёт документ (путь без хоста) или null. */
export function currentEntry(doc) {
  if (!doc || typeof doc.querySelectorAll !== 'function') return null;
  for (const el of doc.querySelectorAll('script[type="module"][src]')) {
    const m = String(el.getAttribute('src') || '').match(ENTRY_RE);
    if (m) return m[0];
  }
  return null;
}

export function isNewBuild(current, served) {
  return !!(current && served && current !== served);
}

export function shouldCheck({ now, lastAt, visible }) {
  if (visible === false) return false;
  return lastAt == null || now - lastAt < 0 || now - lastAt >= MIN_GAP_MS;
}

/** Что отдаёт сервер сейчас (мимо кэша браузера); сбой сети/ответа — null. */
export async function fetchServedEntry(fetchImpl) {
  try {
    const res = await fetchImpl('/', { cache: 'no-store', credentials: 'same-origin',
      headers: { Accept: 'text/html' } });
    if (!res || !res.ok) return null;
    return entryFromHtml(await res.text());
  } catch (_) {
    return null;
  }
}

/** Запустить сверку. `onNewBuild(served)` — один раз на каждую новую сборку. → stop(). */
export function startBuildWatch({ win, doc, fetchImpl, onNewBuild, now = () => Date.now() }) {
  const current = currentEntry(doc);
  if (!current || !win || typeof fetchImpl !== 'function') return () => {};
  let lastAt = null;
  let inFlight = false;
  let announced = null;
  let alive = true;
  const visible = () => !doc || doc.visibilityState !== 'hidden';

  const check = async () => {
    const t = now();
    if (!alive || inFlight || !shouldCheck({ now: t, lastAt, visible: visible() })) return;
    lastAt = t;
    inFlight = true;
    const served = await fetchServedEntry(fetchImpl);
    inFlight = false;
    if (!alive || !isNewBuild(current, served) || served === announced) return;
    announced = served;
    onNewBuild(served);
  };
  const onVisibility = () => { if (visible()) check(); };

  doc.addEventListener('visibilitychange', onVisibility);
  win.addEventListener('focus', check);
  const timer = win.setInterval(check, CHECK_EVERY_MS);
  const first = win.setTimeout(check, FIRST_CHECK_MS);
  return () => {
    alive = false;
    doc.removeEventListener('visibilitychange', onVisibility);
    win.removeEventListener('focus', check);
    win.clearInterval(timer);
    win.clearTimeout(first);
  };
}
